
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/hooks/use-user";
import { toast } from "sonner";

interface CampaignStats {
  id: string;
  title: string;
  goal_amount: number;
  current_amount: number;
  deadline: string;
  cover_image_url: string;
  status: 'active' | 'finished';
  contribution_count: number;
  progress_percentage: number;
}

export default function CampaignsList() {
  const [campaigns, setCampaigns] = useState<CampaignStats[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, isLoading } = useUser();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      console.log("Fetching campaigns...");
      
      // First try to fetch from the campaign_stats view
      let { data: statsData, error: statsError } = await supabase
        .from('campaign_stats')
        .select('*');
      
      if (statsError) throw statsError;
      
      if (statsData && statsData.length > 0) {
        console.log("Found campaigns in campaign_stats view:", statsData);
        setCampaigns(statsData as CampaignStats[]);
      } else {
        // If no data in the view, fetch from the campaigns table directly
        console.log("No data in campaign_stats view, fetching from campaigns table...");
        const { data: campaignsData, error: campaignsError } = await supabase
          .from('campaigns')
          .select('*');
          
        if (campaignsError) throw campaignsError;
        
        if (campaignsData) {
          // Transform the data to match CampaignStats interface
          const transformedData: CampaignStats[] = campaignsData.map(campaign => ({
            id: campaign.id,
            title: campaign.title,
            goal_amount: campaign.goal_amount,
            current_amount: campaign.current_amount || 0,
            deadline: campaign.deadline,
            cover_image_url: campaign.cover_image_url || 'https://via.placeholder.com/300x200?text=Sem+Imagem',
            status: new Date(campaign.deadline) > new Date() ? 'active' : 'finished',
            contribution_count: 0, // Default since we don't have this data
            progress_percentage: campaign.current_amount ? (campaign.current_amount / campaign.goal_amount) * 100 : 0
          }));
          
          console.log("Transformed campaign data:", transformedData);
          setCampaigns(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    if (!user && !isLoading) {
      toast.error('Você precisa estar logado para criar uma campanha');
      navigate('/auth');
      return;
    }
    
    // Navigate to the campaign creation page
    console.log('Navegando para a página de criação de campanha...');
    navigate('/campaigns/create');
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <HandCoins className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Campanhas de Financiamento</h1>
        </div>
        <Button onClick={handleCreateCampaign}>
          Criar Campanha
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((placeholder) => (
            <Card key={placeholder} className="animate-pulse overflow-hidden">
              <div className="aspect-video bg-gray-200"></div>
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded"></div>
              </CardHeader>
              <CardContent>
                <div className="h-2 bg-gray-200 rounded mb-2"></div>
                <div className="flex justify-between">
                  <div className="h-4 w-20 bg-gray-200 rounded"></div>
                  <div className="h-4 w-20 bg-gray-200 rounded"></div>
                </div>
              </CardContent>
              <CardFooter>
                <div className="h-8 w-full bg-gray-200 rounded"></div>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12">
          <HandCoins className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-medium text-gray-600 mb-2">Nenhuma campanha encontrada</h2>
          <p className="text-gray-500 mb-4">Seja o primeiro a criar uma campanha de financiamento</p>
          <Button onClick={handleCreateCampaign}>
            Criar sua campanha
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-video relative">
                <img
                  src={campaign.cover_image_url || 'https://via.placeholder.com/300x200?text=Sem+Imagem'}
                  alt={campaign.title}
                  className="w-full h-full object-cover"
                />
                <Badge 
                  className="absolute top-2 right-2"
                  variant={campaign.status === 'active' ? 'default' : 'secondary'}
                >
                  {campaign.status === 'active' ? 'Em andamento' : 'Finalizada'}
                </Badge>
              </div>
              
              <CardHeader>
                <CardTitle className="line-clamp-1">{campaign.title}</CardTitle>
              </CardHeader>
              
              <CardContent>
                <Progress value={campaign.progress_percentage} className="mb-2" />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>R$ {campaign.current_amount.toLocaleString('pt-BR')}</span>
                  <span>{Math.round(campaign.progress_percentage)}% da meta</span>
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  <span>Meta: R$ {campaign.goal_amount.toLocaleString('pt-BR')}</span>
                </div>
              </CardContent>
              
              <CardFooter className="flex justify-between">
                <span className="text-sm text-muted-foreground">
                  {campaign.contribution_count} apoiadores
                </span>
                <div className="space-x-2">
                  <Button variant="outline" onClick={() => navigate(`/campaigns/${campaign.id}`)}>
                    Ver mais
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
